/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

/**
 * Transfer API Service — Multi-product + Approval Workflow
 * Endpoints for /api/v1/venta/transfers
 */

import { get, post, put } from '@/services/api/client';
import type { ApiResponse } from '@/services/api/types';
import type {
  TransferListItem,
  CreateTransferRequest,
  UpdateTransferRequest,
  RejectTransferRequest,
  CancelTransferRequest,
  TransferResult,
  TransferSummary,
  TransferListParams,
  PendingApprovalsParams,
} from '../types/transfer';
import type { PagedResult } from '../types/product';

const BASE = '/venta/transfers';

export async function createTransfer(data: CreateTransferRequest): Promise<TransferResult> {
  const response = await post<ApiResponse<TransferResult>>(BASE, data);
  return response.data;
}

export async function approveTransfer(id: string): Promise<TransferResult> {
  const response = await post<ApiResponse<TransferResult>>(`${BASE}/${id}/approve`, {});
  return response.data;
}

export async function updateAndResubmitTransfer(id: string, data: UpdateTransferRequest): Promise<TransferResult> {
  const response = await put<ApiResponse<TransferResult>>(`${BASE}/${id}/resubmit`, data);
  return response.data;
}

export async function rejectTransfer(id: string, data: RejectTransferRequest): Promise<TransferResult> {
  const response = await post<ApiResponse<TransferResult>>(`${BASE}/${id}/reject`, data);
  return response.data;
}

export async function cancelTransfer(id: string, data?: CancelTransferRequest): Promise<TransferResult> {
  const response = await post<ApiResponse<TransferResult>>(`${BASE}/${id}/cancel`, data ?? {});
  return response.data;
}

export async function getPendingApprovals(params: PendingApprovalsParams = {}): Promise<PagedResult<TransferListItem>> {
  const queryParams: Record<string, unknown> = {};
  if (params.destinationLocationId) queryParams.destinationLocationId = params.destinationLocationId;
  if (params.page !== undefined) queryParams.page = params.page;
  if (params.size !== undefined) queryParams.size = params.size;

  const response = await get<ApiResponse<PagedResult<TransferListItem>>>(`${BASE}/pending`, queryParams);
  return response.data;
}

export async function getTransfers(params: TransferListParams = {}): Promise<PagedResult<TransferListItem>> {
  const queryParams: Record<string, unknown> = {};
  if (params.status) queryParams.status = params.status;
  if (params.search) queryParams.search = params.search;
  if (params.page !== undefined) queryParams.page = params.page;
  if (params.size !== undefined) queryParams.size = params.size;
  if (params.sort) queryParams.sort = params.sort;
  if (params.direction) queryParams.direction = params.direction;

  const response = await get<ApiResponse<PagedResult<TransferListItem>>>(BASE, queryParams);
  return response.data;
}

export async function getTransferDetail(id: string): Promise<TransferListItem> {
  const response = await get<ApiResponse<TransferListItem>>(`${BASE}/${id}`);
  return response.data;
}

export async function getTransferSummary(): Promise<TransferSummary> {
  const response = await get<ApiResponse<TransferSummary>>(`${BASE}/summary`);
  return response.data;
}

export const transferService = {
  createTransfer,
  approveTransfer,
  updateAndResubmitTransfer,
  rejectTransfer,
  cancelTransfer,
  getPendingApprovals,
  getTransfers,
  getTransferDetail,
  getTransferSummary,
};
